import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Switch } from 'Src/components/Switch/Switch';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { StudentBirthday } from 'Src/components/StudentBirthday/StudentBirthday';
import '../../../AdminView/Tournaments/TournamentsHub/TournamentsHub.css';
import './StudentsToAddToEventForm.scss';
import {RelayPositions, RelayPositionValues, missingRelayPositions} from './RelayPositions';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { SchoolForm } from 'Src/models/form';
import { IndividualTournamentTeamPlayer } from 'Src/models/tournamentTeamPlayer';
import { SchoolEvent } from 'Src/models/event';
import { getEventGeneratedName } from 'Src/helpers/accessor/accessor';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { searchFunctionHighLevelTournamentPlayers } from 'Src/helpers/autocomplete/autocomplete';

interface PlayerToReplace {
  eventId: string;
  playerId: string;
  firstName: string;
  lastName: string;
  eventName: string;
}

interface SelectedStudentItem {
  student: IndividualTournamentTeamPlayer;
  playersToReplace: PlayerToReplace[];
}

interface LimitWarning {
  event: SchoolEvent;
  currentPlayers: any[];
  student: IndividualTournamentTeamPlayer;
  pendingWarnings: SchoolEvent[];
}

interface ParticipationLimitWarning {
  studentId: string;
  studentName: string;
  eventType: 'individual' | 'relay';
  count: number;
  limit: number;
}

interface Props {
  isSaving?: boolean;
  user: AppUser;
  events: SchoolEvent[];
  forms: SchoolForm[];
  tournamentId: string;
  teamId: string;
  addStudentsToEvent: (
    events: SchoolEvent[],
    selectedStudents: IndividualTournamentTeamPlayer[],
    playersToReplace: { eventId: string; playerId: string }[],
    positions?: RelayPositionValues
  ) => void;
  onCloseClick: () => void;
}

interface State {
  relayPositions: RelayPositionValues;
  selectedStudents: SelectedStudentItem[];
  isEventsForYongerGroup: boolean;
  isHelpModalOpen: boolean;
  limitWarning: LimitWarning | null;
}

export class StudentsToAddToEventForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      relayPositions: {},
      selectedStudents: [],
      isEventsForYongerGroup: false,
      isHelpModalOpen: false,
      limitWarning: null
    };
  }

  isLimitEnforced = (event: SchoolEvent) =>
    propz.get<any>(event, ['tournament', 'isEventParticipantLimitEnforced'], true) !== false;

  canOverrideLimit = (event: SchoolEvent) =>
    this.props.user.activeRole === 'ADMIN' &&
    String(this.props.user.activeSchoolId) === String(propz.get(event, ['tournament', 'schoolId'], ''));

  getSchoolPlayersInEvent(event: SchoolEvent): any[] {
    const { user } = this.props;
    const { activeSchool, activeSchoolId } = user;
    const schoolIds: string[] = propz.get(activeSchool, ['schoolIds'], [activeSchoolId]);

    const fromEvent: any[] = (() => {
      const individualsData = (event.individualsData || []).filter(
        p => schoolIds.includes(p.schoolId) || p.schoolId === activeSchoolId
      );
      if (individualsData.length > 0) return individualsData;

      const allTeamPlayers = (event.teamsData || [])
        .filter(team => schoolIds.includes(team.schoolId) || team.schoolId === activeSchoolId)
        .reduce((acc: any[], team) => [...acc, ...(team.players || [])], []);
      if (allTeamPlayers.length > 0) return allTeamPlayers;

      return (event.players || []).filter(p => schoolIds.includes(p.schoolId) || p.schoolId === activeSchoolId);
    })();

    const { selectedStudents } = this.state;

    const alreadySelected = selectedStudents.map(item => ({
      id: item.student.userId,
      userId: item.student.userId,
      permissionId: item.student.permissionId,
      firstName: item.student.firstName,
      lastName: item.student.lastName,
      form: item.student.form,
      schoolId: item.student.schoolId,
      schoolName: item.student.schoolName,
      _isFromCurrentSession: true
    }));

    return [...fromEvent, ...alreadySelected];
  }

  onStudentSelect = (student: IndividualTournamentTeamPlayer) => {
    const { events, user } = this.props;
    const { activeSchoolId } = user;

    const eventsWithLimitReached = events.filter(event => {
      const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
      if (!this.isLimitEnforced(event) || typeof maxPlayers !== 'number' || maxPlayers <= 0) return false;
      const schoolPlayers = this.getSchoolPlayersInEvent(event);
      return schoolPlayers.length >= maxPlayers;
    });

    if (eventsWithLimitReached.length > 0) {
      const canOverrideAll = eventsWithLimitReached.every(event => this.canOverrideLimit(event));
      if (
        canOverrideAll &&
        window.confirm(
          `Warning: adding ${getFullName(student)} will exceed the participant limit in ${
            eventsWithLimitReached.length
          } event${eventsWithLimitReached.length === 1 ? '' : 's'}. Add anyway?`
        )
      ) {
        this.setState(prevState => ({
          selectedStudents: [...prevState.selectedStudents, { student, playersToReplace: [] }]
        }));
        return;
      }
      const firstEvent = eventsWithLimitReached[0];
      const currentPlayers = this.getSchoolPlayersInEvent(firstEvent);
      this.setState({
        limitWarning: {
          event: firstEvent,
          currentPlayers,
          student,
          pendingWarnings: eventsWithLimitReached.slice(1)
        }
      });
      return;
    }

    this.setState(prevState => ({
      selectedStudents: [...prevState.selectedStudents, { student, playersToReplace: [] }]
    }));
  };

  onReplacePlayerSelect = (player: any) => {
    const { limitWarning } = this.state;
    if (!limitWarning) return;

    const { event, student, pendingWarnings } = limitWarning;
    const { user } = this.props;
    const { activeSchoolId } = user;

    const isFromSession = (player as any)._isFromCurrentSession;

    const replacement: PlayerToReplace = {
      eventId: event.id,
      playerId: player.id || player.userId || '',
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      eventName: propz.get(event, ['generatedNames', activeSchoolId], '')
    };

    if (pendingWarnings.length > 0) {
      const nextEvent = pendingWarnings[0];
      const nextPlayers = this.getSchoolPlayersInEvent(nextEvent);
      this.setState(prevState => {
        const nextSelectedStudents = isFromSession
          ? prevState.selectedStudents.filter(item => item.student.userId !== player.userId)
          : prevState.selectedStudents;

        return {
          limitWarning: {
            event: nextEvent,
            currentPlayers: nextPlayers,
            student,
            pendingWarnings: pendingWarnings.slice(1)
          },
          selectedStudents: nextSelectedStudents
        };
      });
    } else {
      this.setState(prevState => {
        const nextSelectedStudents = isFromSession
          ? prevState.selectedStudents.filter(item => item.student.userId !== player.userId)
          : prevState.selectedStudents;

        return {
          selectedStudents: [
            ...nextSelectedStudents,
            { student, playersToReplace: isFromSession ? [] : [replacement] }
          ],
          limitWarning: null
        };
      });
    }
  };

  onRemoveStudent = (studentId: string) => {
    this.setState(prevState => ({
      selectedStudents: prevState.selectedStudents.filter(item => item.student.userId !== studentId)
    }));
  };

  searchFunction = (text: string) => {
    const { user, events, forms, tournamentId, teamId } = this.props;
    const { isEventsForYongerGroup, selectedStudents } = this.state;

    const selectedStudentsIds = selectedStudents.map(item => item.student.userId);

    return searchFunctionHighLevelTournamentPlayers(
      text,
      user,
      events,
      forms,
      tournamentId,
      teamId,
      selectedStudentsIds,
      isEventsForYongerGroup
    );
  };

  getParticipationLimitWarnings = (): ParticipationLimitWarning[] => {
    const { events } = this.props;
    const sourceEvent = events[0];
    const tournament = propz.get<any>(sourceEvent, ['tournament'], null);
    if (!tournament || propz.get<any>(tournament, ['isEventParticipantLimitEnforced'], true) === false) return [];

    const individualLimit = Number(propz.get(tournament, ['maxIndividualEventsPerStudent'], 0));
    const relayLimit = Number(propz.get(tournament, ['maxRelayEventsPerStudent'], 0));

    return this.state.selectedStudents.reduce((warnings, item) => {
      const individualIds = new Set<string>();
      const relayIds = new Set<string>();
      (item.student.events || []).forEach(event => (event.teamBased ? relayIds : individualIds).add(event.id));
      events.forEach(event =>
        (propz.get<boolean>(event, ['sport', 'isRelay'], false) ? relayIds : individualIds).add(event.id)
      );

      const studentName = getFullName(item.student);
      if (individualLimit > 0 && individualIds.size > individualLimit) {
        warnings.push({
          studentId: item.student.userId,
          studentName,
          eventType: 'individual',
          count: individualIds.size,
          limit: individualLimit
        });
      }
      if (relayLimit > 0 && relayIds.size > relayLimit) {
        warnings.push({
          studentId: item.student.userId,
          studentName,
          eventType: 'relay',
          count: relayIds.size,
          limit: relayLimit
        });
      }
      return warnings;
    }, [] as ParticipationLimitWarning[]);
  };

  onSubmit = () => {
    if (this.getParticipationLimitWarnings().length > 0) return;
    if (this.props.events.some(event => missingRelayPositions(event, this.state.selectedStudents.map(item => item.student), this.state.relayPositions[event.id]))) return;

    const { addStudentsToEvent, events } = this.props;
    const { selectedStudents } = this.state;

    const students = selectedStudents.map(item => item.student);
    const playersToReplace = selectedStudents.reduce((acc, item) => {
      return [...acc, ...item.playersToReplace.map(r => ({ eventId: r.eventId, playerId: r.playerId }))];
    }, [] as { eventId: string; playerId: string }[]);

    addStudentsToEvent(events, students, playersToReplace, this.state.relayPositions);
  };

  renderEvents() {
    const { user, events } = this.props;
    return <section className="hltStudentAllocationForm__section" aria-label="Selected events">
      <h3>Adding to {events.length === 1 ? 'event' : 'events'}</h3>
      <div className="hltStudentAllocationForm__events">{events.map(event => {
        const name = getEventGeneratedName(event, {user}) || propz.get(event, ['generatedNames', 'official'], 'Event');
        const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
        const hasLimit = this.isLimitEnforced(event) && typeof maxPlayers === 'number' && maxPlayers > 0;
        const count = this.getSchoolPlayersInEvent(event).length;
        return <span key={event.id} className={`hltStudentAllocationForm__event${hasLimit && count >= maxPlayers ? ' is-full' : ''}`}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14H3V6a2 2 0 0 1 2-2z" /></svg>
          {name}{hasLimit && <small>{count}/{maxPlayers} places filled</small>}
        </span>;
      })}</div>
    </section>;
  }

  renderSelectedStudents() {
    const {selectedStudents} = this.state;
    return <section className="hltStudentAllocationForm__section" aria-label="Selected students">
      <h3>Selected students <span>{selectedStudents.length}</span></h3>
      {!selectedStudents.length ? <div className="hltStudentAllocationForm__empty">No students selected yet. Search above and choose the children to add.</div> :
        <div className="hltStudentAllocationForm__students">{selectedStudents.map(({student, playersToReplace}) =>
          <div key={student.userId} className="hltStudentAllocationForm__student">
            <div className="hltStudentAllocationForm__studentCopy">
              <strong>{getFullName(student)}</strong>
              <small>{student.schoolName || 'School not recorded'}{propz.get(student, ['form', 'name'], '') && ` · ${student.form.name}`}</small>
              <StudentBirthday birthday={student.birthday} labelled />
              {playersToReplace.map((replacement, index) => <span className="hltStudentAllocationForm__replacement" key={index}>
                Replaces {replacement.lastName} {replacement.firstName} in {replacement.eventName}
              </span>)}
            </div>
            <button type="button" className="hltStudentAllocationForm__remove" aria-label={`Remove ${getFullName(student)} from selection`}
              onClick={() => this.onRemoveStudent(student.userId)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
            </button>
          </div>)}</div>}
    </section>;
  }

  renderLimitWarningModal() {
    const {limitWarning} = this.state;
    if (!limitWarning) return null;
    const {event, currentPlayers, student, pendingWarnings} = limitWarning;
    const eventName = getEventGeneratedName(event, {user: this.props.user}) || propz.get(event, ['generatedNames', 'official'], 'Event');
    const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
    return <SimpleModal isOpen title="Participant limit reached" customClass="hltStudentAllocationModal" dialogStyle={{maxWidth: 560}}
      isBodyBold={false} showFooter={false} onCloseClick={() => this.setState({limitWarning: null})}>
      <div className="hltStudentAllocationForm">
        <div className="hltStudentAllocationForm__content">
          <div className="hltStudentAllocationForm__notice" role="alert">
            <strong>{eventName} is full</strong>
            <p>{currentPlayers.length} of {maxPlayers} places are filled by your organisation. Choose a student to replace with {getFullName(student)}.</p>
            {pendingWarnings.length > 0 && <small>{pendingWarnings.length} more events need a replacement.</small>}
          </div>
          <div className="hltStudentAllocationForm__students">{currentPlayers.map((player, index) => <div className="hltStudentAllocationForm__student" key={player.id || player.userId || index}>
            <div className="hltStudentAllocationForm__studentCopy"><strong>{getFullName(player)}</strong>
              <small>{player.schoolName || 'School not recorded'}</small>
              {player._isFromCurrentSession && <small>Selected in this session</small>}
            </div>
            <button type="button" className="tnBtn tnBtnSecondary" onClick={() => this.onReplacePlayerSelect(player)}>Replace</button>
          </div>)}</div>
        </div>
        <footer className="hltStudentAllocationForm__footer"><button type="button" className="tnBtn tnBtnSecondary" onClick={() => this.setState({limitWarning: null})}>Cancel</button></footer>
      </div>
    </SimpleModal>;
  }

  render() {
    const {onCloseClick} = this.props;
    const {isEventsForYongerGroup, selectedStudents} = this.state;
    const warnings = this.getParticipationLimitWarnings();
    return <fieldset className="hltStudentAllocationForm" disabled={this.props.isSaving} aria-busy={this.props.isSaving}>
      <HelpModal isOpen={this.state.isHelpModalOpen} title="Add students to events" badgeText="Allocation"
        subtitle="Search and select students to add to the selected events." onClose={() => this.setState({isHelpModalOpen: false})}>
        <p>Select eligible students, review participant limits and save the completed allocation. If an event is full, choose which current participant should be replaced.</p>
      </HelpModal>
      {this.renderLimitWarningModal()}
      <div className="hltStudentAllocationForm__content">
        <div className="hltStudentAllocationForm__intro"><p>Choose students from your tournament team. You can add several students before saving.</p>
          <HelpIconButton onClick={() => this.setState({isHelpModalOpen: true})} label="How adding students to an event works" />
        </div>
        {this.renderEvents()}
        {warnings.length > 0 && <div className="hltStudentAllocationForm__notice is-error" role="alert">
          <strong>Participation limit exceeded</strong><p>Remove the students listed below before saving.</p>
          <ul>{warnings.map(warning => <li key={`${warning.studentId}-${warning.eventType}`}>
            {warning.studentName} would be in {warning.count} {warning.eventType} events; maximum {warning.limit}.
          </li>)}</ul>
        </div>}
        <section className="hltStudentAllocationForm__section">
          <div className="hltStudentAllocationForm__searchHeading"><strong>Search students</strong>
            <Switch value={isEventsForYongerGroup} onChange={() => this.setState({isEventsForYongerGroup: !isEventsForYongerGroup})}
              text="Include younger children" name="ageGroup" />
          </div>
          <Autocomplete disabled={this.props.isSaving} searchFunction={this.searchFunction} clearOnSelect
            renderItem={item => <div className="hltStudentAllocationForm__option"><strong>{getFullName(item)}</strong><small>{item.schoolName || 'School not recorded'} {propz.get(item, ['form', 'name'], '')}</small></div>}
            getElementTitle={item => `${getFullName(item)} ${propz.get(item, ['form', 'name'], '')}`}
            customClass="mFullWidth" placeholder="Start typing the student name" onSelect={this.onStudentSelect} />
          <p className="hltStudentAllocationForm__hint">Only eligible students who have not already been added are shown.</p>
        </section>
        {this.renderSelectedStudents()}
        {this.props.events.map(event => <RelayPositions key={event.id} event={event}
          eventName={getEventGeneratedName(event, {user: this.props.user}) || propz.get(event, ['generatedNames', 'official'], 'Event')}
          students={selectedStudents.map(item => item.student)} values={this.state.relayPositions[event.id]}
          onChange={(key, value) => this.setState(state => ({relayPositions: {...state.relayPositions,
            [event.id]: {...state.relayPositions[event.id], [key]: value}}}))} />)}
      </div>
      <footer className="hltStudentAllocationForm__footer">
        <span>{selectedStudents.length} student{selectedStudents.length === 1 ? '' : 's'} selected</span>
        <div><button type="button" className="tnBtn tnBtnSecondary" onClick={onCloseClick}>Cancel</button>
          <button type="button" className="tnBtn tnBtnPrimary" disabled={!selectedStudents.length || warnings.length > 0 || this.props.events.some(event => missingRelayPositions(event, selectedStudents.map(item => item.student), this.state.relayPositions[event.id]))} onClick={this.onSubmit}>
            {selectedStudents.length ? `Add ${selectedStudents.length} student${selectedStudents.length === 1 ? '' : 's'}` : 'Add students'}
          </button></div>
      </footer>
    </fieldset>;
  }
}
